import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Building2, Loader2 } from "lucide-react";
import "./Department.css";
axios.defaults.withCredentials = true;

const Department = () => {
  const [departments, setDepartments] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [showAddDeptConfirm, setShowAddDeptConfirm] = useState(false);
  const [addDeptPopupError, setAddDeptPopupError] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get("/department", {
        withCredentials: true,
      });
      setDepartments(res.data);
      setError(null);
    } catch (err) {
      handleAuthError(err, "ไม่สามารถโหลดข้อมูลแผนกได้");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const handleAuthError = (err, fallbackMsg) => {
    console.error(fallbackMsg, err);
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      setError(
        "คุณไม่มีสิทธิ์เข้าถึงเมนูนี้ (กรุณาเข้าสู่ระบบในสิทธิ์ผู้ดูแล)",
      );
    } else {
      setError(fallbackMsg);
    }
  };

  const isDuplicate = (name, ignoreId = null) => {
    return departments.some(
      (dept) =>
        (dept.name || "").toLowerCase() === name.toLowerCase() &&
        (ignoreId ? dept.id !== ignoreId : true),
    );
  };

  const handleAddDepartment = () => {
    setNewDept("");
    setError(null);
    setAddDeptPopupError("");
    setShowAddDeptConfirm(true);
  };

  const confirmAddDepartment = async () => {
    const trimmedName = newDept.trim();

    if (!trimmedName) {
      setAddDeptPopupError("กรุณากรอกชื่อแผนก");
      return;
    }

    if (isDuplicate(trimmedName)) {
      setAddDeptPopupError(`แผนก "${trimmedName}" มีอยู่แล้วในระบบ`);
      return;
    }

    try {
      setIsAdding(true);

      await axios.post(
        "/department",
        { name: trimmedName },
        { withCredentials: true },
      );

      setNewDept("");
      setAddDeptPopupError("");
      setShowAddDeptConfirm(false);
      setError(null);

      fetchDepartments();
    } catch (err) {
      handleAuthError(err, "ไม่สามารถเพิ่มแผนกได้ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setIsAdding(false);
    }
  };

  const cancelAddDepartment = () => {
    setShowAddDeptConfirm(false);
    setNewDept("");
    setAddDeptPopupError("");
  };

  // const startEdit = (dept) => {
  //   setEditingId(dept.id);
  //   setEditingName(dept.name);
  // };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveEdit = async (id) => {
    const name = editingName.trim();
    if (!name) return;
    if (isDuplicate(name, id)) {
      setError(`แผนก "${name}" มีอยู่แล้วในระบบ`);
      return;
    }
    try {
      await axios.put(
        `/department/${id}`,
        { name },
        { withCredentials: true },
      );
      setError(null);
      cancelEdit();
      fetchDepartments();
    } catch (err) {
      handleAuthError(err, "ไม่สามารถแก้ไขแผนกได้");
    }
  };

  // const deleteDepartment = async (id, name) => {
  //   if (!window.confirm(`ยืนยันการลบแผนก "${name}" ?`)) return;
  //   try {
  //     await axios.delete(`/department/${id}`, {
  //       withCredentials: true,
  //     });
  //     setError(null);
  //     fetchDepartments();
  //   } catch (err) {
  //     const status = err?.response?.status;
  //     if (status === 409) {
  //       setError("ลบไม่ได้: แผนกนี้ถูกอ้างอิงโดยข้อมูลอื่น (เช่น พนักงาน)");
  //     } else {
  //       handleAuthError(err, "ไม่สามารถลบแผนกได้");
  //     }
  //   }
  // };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") confirmAddDepartment();
  };

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="department-container">
      <div className="department-header">
        <h2 className="cus-info-title">Department Management</h2>
      </div>

      {error && (
        <div className="department-error">
          <p>
            <span>⚠️</span>
            {error}
          </p>
        </div>
      )}

        <div className="add-department-header-row">
          <button
            type="button"
            onClick={handleAddDepartment}
            disabled={isAdding}
            className="department-button"
          >
            {isAdding ? (
              <Loader2 className="department-spinner" />
            ) : (
              <>
                <Plus className="button-icon" />
                เพิ่มแผนก
              </>
            )}
          </button>
        </div>
      <p className="dep-detail">*กรณีต้องการเปลี่ยนชื่อแผนก กรุณาติดต่อผู้ดูแลระบบ เนื่องจากระบบมีการผูกกับข้อมูลการส่งอีเมลขององค์กรอยู่</p>

      <div className="departments-list-container">
        <h3>รายชื่อแผนก</h3>

        {loading ? (
          <div className="loading-container">
            <Loader2 className="loading-spinner" />
            <span>กำลังโหลดข้อมูล...</span>
          </div>
        ) : departments.length === 0 ? (
          <div className="empty-departments">
            <p>ยังไม่มีแผนกในระบบ</p>
            <p className="empty-departments-subtitle">
              เริ่มต้นด้วยการเพิ่มแผนกใหม่
            </p>
          </div>
        ) : (
          <ul className="departments-list">
            {departments.map((dept) => (
              <li key={dept.id} className="department-item">
                <Building2 className="department-icon" />

                {editingId === dept.id ? (
                  <>
                    <input
                      className="department-input inline"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" ? saveEdit(dept.id) : null
                      }
                      autoFocus
                    />
                    <div className="actions right">
                      <button
                        className="text-btn"
                        onClick={() => saveEdit(dept.id)}
                      >
                        บันทึก
                      </button>
                      <button className="text-btn muted" onClick={cancelEdit}>
                        ยกเลิก
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="dept-name">{dept.name}</span>
                    {/* <div className="actions right">
                      <button
                        className="text-btn"
                        onClick={() => startEdit(dept)}
                      >
                        แก้ไข
                      </button>
                      <button
                        className="text-btn danger"
                        onClick={() => deleteDepartment(dept.id, dept.name)}
                      >
                        ลบ
                      </button>
                    </div> */}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {showAddDeptConfirm && (
        <div className="dept-confirm-overlay">
          <div className="dept-confirm-modal">
            <h3>เพิ่มแผนกใหม่</h3>

            <p>กรอกชื่อแผนกที่ต้องการเพิ่มเข้าระบบ</p>

            <div className="dept-popup-field">
              <label>ชื่อแผนก</label>

              <input
                type="text"
                value={newDept}
                onChange={(e) => {
                  setNewDept(e.target.value);
                  setAddDeptPopupError("");
                }}
                onKeyDown={handleKeyPress}
                placeholder="ชื่อแผนกใหม่..."
                className="department-input"
                disabled={isAdding}
                autoFocus
              />

              {addDeptPopupError && (
                <div className="dept-popup-error">{addDeptPopupError}</div>
              )}
            </div>

            <div className="dept-confirm-actions">
              <button
                type="button"
                className="dept-confirm-save-btn"
                onClick={confirmAddDepartment}
                disabled={isAdding}
              >
                {isAdding ? "กำลังเพิ่ม..." : "ยืนยันเพิ่ม"}
              </button>

              <button
                type="button"
                className="dept-confirm-cancel-btn"
                onClick={cancelAddDepartment}
                disabled={isAdding}
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Department;
